import { AlertTriangle, CheckCircle2, TrendingDown } from "lucide-react";
import React, { useMemo } from "react";
import { generateForecastData } from "@/data/demo-data";
import {
  BANK_ACCOUNTS,
  MONTHLY_OPERATING_EXPENSES,
  MONTHLY_REVENUE,
} from "@/data/financial-systems";
import { useFormatting } from "@/hooks/useFormatting";
import { useTerminology } from "@/hooks/useTerminology";

interface RatioRow {
  label: string;
  value: string;
  benchmark: string;
  status: "good" | "warning" | "critical";
  description: string;
}

export const LiquidityAnalysisPreview: React.FC = () => {
  const {
    getCashDurationTerm,
    getCashDurationShortTerm,
    getCashMovementTerms,
  } = useTerminology();
  const { formatCurrency } = useFormatting();
  const terms = getCashMovementTerms();

  // Current figures
  const cashAndEquivalents = BANK_ACCOUNTS.reduce(
    (sum, a) => sum + a.balance,
    0,
  );
  const currentReceivables = 328_500; // outstanding invoices
  const overdueReceivables = 63_200; // overdue invoices
  const currentLiabilities = 125_000; // outstanding bills
  const totalCurrentAssets = cashAndEquivalents + currentReceivables;
  const workingCapital = totalCurrentAssets - currentLiabilities;

  // Liquidity ratios
  const currentRatio = totalCurrentAssets / currentLiabilities;
  const quickRatio =
    (cashAndEquivalents + currentReceivables) / currentLiabilities;
  const cashRatio = cashAndEquivalents / currentLiabilities;
  const coverageMonths = cashAndEquivalents / MONTHLY_OPERATING_EXPENSES;
  const dso = (currentReceivables / (MONTHLY_REVENUE * 12)) * 365; // Days Sales Outstanding

  const ratios: RatioRow[] = [
    {
      label: "Current Ratio",
      value: currentRatio.toFixed(2),
      benchmark: "> 1.5 target",
      status:
        currentRatio >= 1.5
          ? "good"
          : currentRatio >= 1.0
            ? "warning"
            : "critical",
      description:
        "Current assets ÷ current liabilities. Measures short-term solvency.",
    },
    {
      label: "Quick Ratio",
      value: quickRatio.toFixed(2),
      benchmark: "> 1.0 target",
      status:
        quickRatio >= 1.0 ? "good" : quickRatio >= 0.7 ? "warning" : "critical",
      description:
        "Liquid assets only (excludes inventory). Stricter liquidity measure.",
    },
    {
      label: "Cash Ratio",
      value: cashRatio.toFixed(2),
      benchmark: "> 0.5 target",
      status:
        cashRatio >= 0.5 ? "good" : cashRatio >= 0.2 ? "warning" : "critical",
      description:
        "Cash only ÷ current liabilities. Most conservative liquidity measure.",
    },
    {
      label: `Cash ${getCashDurationTerm()}`,
      value: `${coverageMonths.toFixed(1)} months`,
      benchmark: "> 3 months target",
      status: coverageMonths >= 3 ? "warning" : "critical",
      description:
        "How long current cash covers operating costs at the current burn rate.",
    },
    {
      label: "Days Sales Outstanding (DSO)",
      value: `${Math.round(dso)} days`,
      benchmark: "< 45 days target",
      status: dso <= 45 ? "good" : dso <= 60 ? "warning" : "critical",
      description:
        "Average number of days to collect payment after invoice issue.",
    },
  ];

  const statusIcon = {
    good: <CheckCircle2 className="h-4 w-4 text-green-600" />,
    warning: <AlertTriangle className="h-4 w-4 text-amber-500" />,
    critical: <TrendingDown className="h-4 w-4 text-red-600" />,
  };

  const statusColour = {
    good: "text-green-700 dark:text-green-400",
    warning: "text-amber-700 dark:text-amber-400",
    critical: "text-red-700 dark:text-red-400",
  };

  return (
    <div className="space-y-6">
      {/* Working capital summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="text-xs text-muted-foreground mb-1">
            Cash &amp; Equivalents
          </p>
          <p className="text-xl font-bold">
            {formatCurrency(cashAndEquivalents)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">3 bank accounts</p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="text-xs text-muted-foreground mb-1">
            Trade Receivables
          </p>
          <p className="text-xl font-bold">
            {formatCurrency(currentReceivables)}
          </p>
          <p className="text-xs text-amber-600 mt-1">
            {formatCurrency(overdueReceivables)} overdue
          </p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="text-xs text-muted-foreground mb-1">Trade Payables</p>
          <p className="text-xl font-bold">
            {formatCurrency(currentLiabilities)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Outstanding supplier bills
          </p>
        </div>
        <div
          className={`rounded-lg border p-4 ${workingCapital >= 0 ? "bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800" : "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800"}`}
        >
          <p className="text-xs text-muted-foreground mb-1">Working Capital</p>
          <p
            className={`text-xl font-bold ${workingCapital >= 0 ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}
          >
            {formatCurrency(workingCapital)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Net current assets
          </p>
        </div>
      </div>

      {/* Ratio analysis */}
      <div>
        <p className="text-sm font-semibold mb-3">Liquidity Ratios</p>
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/60">
                <th className="px-4 py-3 text-left font-semibold">Ratio</th>
                <th className="px-4 py-3 text-right font-semibold">Value</th>
                <th className="px-4 py-3 text-right font-semibold">
                  Benchmark
                </th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {ratios.map((row, i) => (
                <tr
                  key={i}
                  className="border-b hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3">
                    <p className="font-medium">{row.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {row.description}
                    </p>
                  </td>
                  <td
                    className={`px-4 py-3 text-right font-bold text-base ${statusColour[row.status]}`}
                  >
                    {row.value}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                    {row.benchmark}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {statusIcon[row.status]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recommendations */}
      <div className="rounded-lg border border-amber-200 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-800 px-4 py-3">
        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">
          Key Liquidity Observations
        </p>
        <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
          <li>
            Working capital is positive at {formatCurrency(workingCapital)},
            indicating short-term solvency
          </li>
          <li>
            Cash {getCashDurationShortTerm()} of {coverageMonths.toFixed(1)}{" "}
            months is below the 3-month target — collections must accelerate
          </li>
          <li>
            DSO of {Math.round(dso)} days is above target — overdue receivables
            of {formatCurrency(overdueReceivables)} require prompt follow-up
          </li>
          <li>
            Cash ratio of {cashRatio.toFixed(2)} is satisfactory — no immediate
            liquidity risk
          </li>
        </ul>
      </div>
    </div>
  );
};
